<script setup>
  import AppSidebar from "@/components/App/Sidebar/AppSidebar.vue";
    import { computed, ref } from "vue";
  import {
    Menu,
    Pen,
    CircleCheck,
    Target,
    Users,
    Link,
    Send,
    SlidersHorizontal,
    File,
    Scale,
    Lightbulb,
    Settings,
    LayoutDashboard,
    ShieldCheck,
    Download,
    LayoutGrid,
    BookOpen,
  } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { userType, access } from "@utils/constants/userRoles";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  import { useSurveyStore } from "@stores/survey/survey.js";
  const surveyStore = useSurveyStore();
  const currentUserStore = useCurrentUserStore();
  // Everything here gates on the accesses resolved for the survey being viewed,
  // so a role held on another survey never unlocks this one.
  const { token, survey, surveyAccesses, canWriteSurvey, canMonitorSurvey, canAnalyzeSurvey } = storeToRefs(surveyStore);
  const { user, isClient } = storeToRefs(currentUserStore);

  const route = useRoute();

  // Editing this survey is enough to manage who reaches it; managing its account
  // works too. Both are read from the accesses resolved for this survey.
  const canManageMembers = computed(
    () => surveyAccesses.value.includes(access.SURVEYS_WRITE) || surveyAccesses.value.includes(access.ACCOUNTS_MANAGE),
  );

  const hasAccountAccess = computed(() => canWriteSurvey.value || canAnalyzeSurvey.value);
  const hasWriteAccess = canWriteSurvey;
  const hasMonitorAccess = canMonitorSurvey;
  const hasSalesAccess = computed(() =>
    [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
  );
  const hasEmailingAccess = computed(
    () =>
      [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type) ||
      (survey.value?.has_mailing_access === true && canWriteSurvey.value),
  );

  // A freemium demo copy is read-only *for the prospect*: Recruit and Settings
  // are hidden, Design collapses to Review only. Episto staff (admin /
  // super_admin / sales) keep the full sidebar so they can inspect, fix or
  // delete a borked demo — mirroring SurveyPolicy#mutable? which already lets
  // admins mutate a freemium_demo survey through the API.
  const freemiumDemo = computed(() => survey.value?.freemium_demo === true && isClient.value);

  const navigation = computed(() => {
    const sections = [
    {
      title: "Design",
      isOpen: ref(true),
      items: [
        {
          title: "Setup",
          url: `/surveys/${token.value}/setup`,
          icon: Menu,
          // use a regex
          isActive: !!route.path.match(/^\/surveys\/\w+\/setup/),
          if: !freemiumDemo.value && (hasSalesAccess.value || (hasAccountAccess.value && hasWriteAccess.value)),
        },
        {
          title: "Context",
          url: `/surveys/${token.value}/context`,
          icon: BookOpen,
          isActive: !!route.path.match(/^\/surveys\/\w+\/context/),
          if: !freemiumDemo.value && (hasSalesAccess.value || (hasAccountAccess.value && hasWriteAccess.value)),
        },
        {
          title: "Questions",
          url: `/surveys/${token.value}/questions#edit`,
          icon: Pen,
          isActive: !!route.path.match(/^\/surveys\/\w+\/questions/),
          if: !freemiumDemo.value && (hasSalesAccess.value || (hasAccountAccess.value && hasWriteAccess.value)),
        },
        {
          title: "Review",
          url: `/surveys/${token.value}/questions#review`,
          icon: CircleCheck,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/questions/),
          if: freemiumDemo.value || (isClient.value && !canWriteSurvey.value && canAnalyzeSurvey.value),
        },
        {
          title: "Targets & Quotas",
          url: `/surveys/${token.value}/target`,
          icon: Target,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/target/),
          if: !freemiumDemo.value && (hasSalesAccess.value || (hasAccountAccess.value && hasWriteAccess.value)),
        },
        {
          title: "Fake respondents",
          url: `/surveys/${token.value}/profiles`,
          icon: Users,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/profiles/),
          if: !freemiumDemo.value && (hasSalesAccess.value || (hasAccountAccess.value && hasWriteAccess.value)),
        },
      ],
    },
    {
      title: "Recruit",
      isOpen: ref(true),
      items: [
        {
          title: "Links",
          url: `/surveys/${token.value}/link`,
          icon: Link,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/link/),
          if: hasSalesAccess.value || hasAccountAccess.value,
        },
        {
          title: "Emailing",
          icon: Send,
          if: hasEmailingAccess.value,
          children: [
            {
              title: "Lists",
              url: `/surveys/${token.value}/mailing/lists`,
              isActive: !!route.path.match(/^\/surveys\/(.*)\/mailing\/lists/),
              if: hasEmailingAccess.value,
            },
            {
              title: "Campaigns",
              url: `/surveys/${token.value}/mailing/campaigns`,
              isActive: !!route.path.match(/^\/surveys\/(.*)\/mailing\/campaigns/),
              if: hasEmailingAccess,
            },
            {
              title: "Test contacts",
              url: `/surveys/${token.value}/mailing/tests`,
              isActive: !!route.path.match(/^\/surveys\/(.*)\/mailing\/tests/),
              if: hasEmailingAccess.value,
            },
          ],
        },
        {
          title: "Monitor",
          icon: SlidersHorizontal,
          if: hasMonitorAccess.value || canAnalyzeSurvey.value,
          children: [
            {
              title: "Progress",
              url: `/surveys/${token.value}/progress`,
              isActive: !!route.path.match(/^\/surveys\/(.*)\/progress/),
              if: hasMonitorAccess.value,
            },
            {
              title: "Completion",
              url: `/surveys/${token.value}/completion`,
              isActive: !!route.path.match(/^\/surveys\/(.*)\/completion/),
              if: canAnalyzeSurvey.value,
            },
          ],
        },
      ],
    },
    {
      title: "Analyze",
      isOpen: ref(true),
      items: [
        {
          title: "Dashboards",
          url: `/surveys/${token.value}/dashboards`,
          icon: LayoutDashboard,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/dashboards/),
          if: canAnalyzeSurvey.value,
        },
        {
          title: "Exports",
          url: `/surveys/${token.value}/exports`,
          icon: Download,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/exports/),
          if: canAnalyzeSurvey.value,
        },
        {
          title: "Grids",
          url: `/surveys/${token.value}/grids`,
          icon: LayoutGrid,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/grids/),
          if:
            canAnalyzeSurvey.value && survey.value.ai_chat === true,
        },
        {
          title: "AI Insights",
          url: `/surveys/${token.value}/insights`,
          icon: AiStar,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/insights/),
          if: canAnalyzeSurvey.value,
        },
        {
          title: "Reports",
          url: `/surveys/${token.value}/reports`,
          icon: File,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/reports/),
          if: canAnalyzeSurvey.value,
        },
        {
          title: "Weighting",
          url: `/surveys/${token.value}/weighting`,
          icon: Scale,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/weighting/),
          if: canAnalyzeSurvey.value,
        },
        {
          title: "Learnings",
          url: `/surveys/${token.value}/learnings`,
          icon: Lightbulb,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/learnings/),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "Quality checks",
          url: `/surveys/${token.value}/quality-checks`,
          icon: ShieldCheck,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/quality-checks/),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
      ],
    },
    {
      title: "Settings",
      isOpen: ref(true),
      items: [
        {
          title: "Settings",
          url: `/surveys/${token.value}/results/setup`,
          icon: Settings,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/results\/setup/),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Members",
          url: `/surveys/${token.value}/users`,
          icon: Users,
          isActive: !!route.path.match(/^\/surveys\/(.*)\/users/),
          if: canManageMembers.value,
        },
      ],
    },
    ];

    if (freemiumDemo.value) {
      return sections.filter((section) => !["Recruit", "Settings"].includes(section.title));
    }

    return sections;
  });
</script>

<template>
  <AppSidebar :navigation="navigation" />
</template>
